/**
 * /table · RecentSearches — localStorage-backed strip on the home view.
 *
 * Quietly stores the last 5 free-text searches a member ran. Renders
 * a slim, on-brand row below the search bar so members can refire
 * without retyping. No backend, no PII leak — lives entirely in the
 * member's browser.
 *
 * Storage shape: `lc.table.recent.v1 = JSON.stringify([{q, t}, ...])`
 * Capped at 5 entries; insertion-order; duplicates de-bounced.
 */
import React, { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "lc.table.recent.v1";
const MAX_ENTRIES = 5;

function readStore() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((e) => e && typeof e.q === "string");
    } catch {
        return [];
    }
}

function writeStore(entries) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
        /* ignore quota / private-mode errors */
    }
}

/** Public helper · imported by Table.jsx so a successful search
 * (regardless of which surface fired it) pushes the query into the
 * recent-searches store. */
export function rememberRecentSearch(q) {
    const text = (q || "").trim();
    if (text.length < 2) return;
    const now = Date.now();
    const cur = readStore().filter(
        (e) => e.q.toLowerCase() !== text.toLowerCase()
    );
    cur.unshift({ q: text, t: now });
    writeStore(cur.slice(0, MAX_ENTRIES));
}

export function RecentSearches({ onPick }) {
    const [entries, setEntries] = useState(() => readStore());

    // Listen for cross-tab updates + same-tab self-updates triggered
    // via `window.dispatchEvent(new Event("lc-recent-update"))`.
    useEffect(() => {
        const refresh = () => setEntries(readStore());
        window.addEventListener("storage", refresh);
        window.addEventListener("lc-recent-update", refresh);
        return () => {
            window.removeEventListener("storage", refresh);
            window.removeEventListener("lc-recent-update", refresh);
        };
    }, []);

    const clearOne = useCallback((q) => {
        const next = readStore().filter(
            (e) => e.q.toLowerCase() !== q.toLowerCase()
        );
        writeStore(next);
        setEntries(next);
    }, []);

    if (entries.length === 0) return null;

    return (
        <div
            data-testid="recent-searches"
            className="t-rise"
            style={{
                animationDelay: "0.3s",
                marginTop: 18,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
            }}
        >
            <span
                className="t-mono uppercase"
                style={{
                    fontSize: 10,
                    letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                    marginRight: 4,
                }}
            >
                recently
            </span>
            {entries.map((e, i) => (
                <span
                    key={e.q + i}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        background: "var(--t-card)",
                        border: "1px solid var(--t-line2)",
                        borderRadius: 999,
                        paddingLeft: 12,
                        paddingRight: 6,
                        height: 28,
                        gap: 6,
                    }}
                >
                    <button
                        type="button"
                        data-testid={`recent-search-${i}`}
                        onClick={() => onPick?.(e.q)}
                        style={{
                            background: "transparent",
                            border: 0,
                            color: "var(--t-ink2)",
                            fontSize: 12,
                            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            cursor: "pointer",
                            padding: 0,
                            whiteSpace: "nowrap",
                            maxWidth: 220,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {e.q}
                    </button>
                    <button
                        type="button"
                        data-testid={`recent-search-clear-${i}`}
                        onClick={() => clearOne(e.q)}
                        aria-label={`Forget search ${e.q}`}
                        style={{
                            background: "transparent",
                            border: 0,
                            color: "var(--t-ink3)",
                            fontSize: 14,
                            lineHeight: 1,
                            cursor: "pointer",
                            padding: "0 4px",
                            opacity: 0.7,
                        }}
                    >
                        ×
                    </button>
                </span>
            ))}
        </div>
    );
}
